/**
 * BR-06 add-funds card and BR-07 checkout return.
 *
 * After staff approve the brief, Pay now prices the remaining campaign
 * total (plus the platform fee) and opens card checkout. When staff have
 * enabled complimentary runs on the organisation, Request a free run sits
 * beside it and asks staff to fund the pool without a card charge. There
 * is no separate quote step - a stale quote was not updating when the
 * brief changed. A forged ?success= URL does not mark the campaign funded.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Eyebrow, Money, PageHeader, StateBlock } from "@ui/primitives";
import { FieldTip, FormError } from "@ui/forms";
import { formatMoney, fromMinorString } from "@contracts/money";
import { fontSize, space } from "@design/index";
import { api } from "@app/shared/api";
import { liveFundingSplit, liveRemainingMinor } from "./draftForm";

interface Quote {
  id: string;
  currency: string;
  gross_minor: string | number;
  net_campaign_minor: string | number;
  fee_minor: string | number;
  tax_minor: string | number;
  expires_at: string;
  allocated?: boolean;
}

interface FundingPreview {
  currency: string;
  campaign_total_minor: string | null;
  funded_minor: string;
  transferred_in_minor?: string;
  transferred_from?: string | null;
  remaining_minor: string | null;
  fee_percent: string;
  fee_rate: string;
  tax_rate: string;
  min_charge_minor?: string;
  chargeable?: boolean;
  payable?: boolean;
  campaign_state?: string | null;
  free_run_enabled?: boolean;
  free_run_available?: boolean;
  free_run?: {
    id: string;
    status: string;
    requested_at: string;
    net_minor?: string | null;
  } | null;
  split: {
    gross_minor: string;
    net_minor: string;
    fee_minor: string;
    tax_minor: string;
  } | null;
}

const minor = (v: string | number, ccy: string) =>
  fromMinorString(String(v), ccy as "GBP" | "USD" | "EUR");

export function FundingCard({ campaignId, currency, campaignState, onFunded, liveTotalMinor }: {
  campaignId: string; currency: string; campaignState?: string;
  onFunded?: () => void;
  liveTotalMinor?: number | null;
}) {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [preview, setPreview] = useState<FundingPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const notified = useRef(false);

  const load = useCallback(async () => {
    const r = await api.get<{ quotes: Quote[]; preview?: FundingPreview }>(
      `/campaigns/${campaignId}/funding-quotes`);
    if (r.ok) {
      setQuotes(r.data.quotes);
      setPreview(r.data.preview ?? null);
      if (!notified.current && r.data.quotes.some((q) => q.allocated)) {
        notified.current = true;
        onFunded?.();
      }
    }
  }, [campaignId, onFunded]);
  useEffect(() => { void load(); }, [load]);

  async function pay() {
    setBusy(true); setError(null);
    const r = await api.post<{ checkoutUrl: string }>(`/campaigns/${campaignId}/pay`, {});
    setBusy(false);
    if (!r.ok) {
      const ref = r.correlationId ? ` Reference ${r.correlationId}.` : "";
      setError(`${r.message}${ref}`);
      return;
    }
    window.location.assign(r.data.checkoutUrl);
  }

  async function requestFreeRun() {
    setRequesting(true); setError(null);
    const r = await api.post<{ request: { id: string; status: string } }>(
      `/campaigns/${campaignId}/free-run-request`, {});
    setRequesting(false);
    if (!r.ok) {
      const ref = r.correlationId ? ` Reference ${r.correlationId}.` : "";
      setError(`${r.message}${ref}`);
      return;
    }
    await load();
  }

  const ccy = preview?.currency ?? currency;
  const funded = Number(preview?.funded_minor ?? 0);
  const transferredIn = Number(preview?.transferred_in_minor ?? 0);
  const feeRate = Number(preview?.fee_rate ?? 0);
  const taxRate = Number(preview?.tax_rate ?? 0);
  const remaining = liveTotalMinor != null
    ? liveRemainingMinor(liveTotalMinor, Number.isFinite(funded) ? funded : 0)
    : preview?.remaining_minor == null ? null : Number(preview.remaining_minor);
  const liveSplit = remaining != null
    ? liveFundingSplit(remaining, Number.isFinite(feeRate) ? feeRate : 0,
        Number.isFinite(taxRate) ? taxRate : 0)
    : null;
  const shown = remaining === 0 ? null : liveSplit ? {
    net: liveSplit.netMinor,
    fee: liveSplit.feeMinor,
    tax: liveSplit.taxMinor,
    gross: liveSplit.grossMinor,
    currency: ccy,
  } : preview?.split ? {
    net: preview.split.net_minor,
    fee: preview.split.fee_minor,
    tax: preview.split.tax_minor,
    gross: preview.split.gross_minor,
    currency: preview.currency,
  } : null;
  const fundedEnough = remaining === 0;
  const minGross = Number(preview?.min_charge_minor ?? 50);
  const liveGross = shown ? Number(shown.gross) : NaN;
  const belowCardMin = Number.isFinite(liveGross) && liveGross > 0 && liveGross < minGross;
  const payable = preview?.payable
    ?? (campaignState === "APPROVED" || campaignState === "ACTIVE");
  const canPay = payable && !fundedEnough && remaining != null && remaining > 0 && !belowCardMin;
  const freeRunStatus = preview?.free_run?.status ?? null;
  const freeRunPending = freeRunStatus === "PENDING";
  const canRequestFree = Boolean(preview?.free_run_available)
    && !fundedEnough && remaining != null && remaining > 0 && !freeRunPending;

  return (
    <Card padding={18} style={{ display: "grid", gap: space(3) }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Eyebrow>Add funds</Eyebrow>
        <FieldTip text={[
          `You pay the remaining campaign total plus the platform fee${preview ? ` (${preview.fee_percent}%)` : ""}. Already paid and leftover moved from another campaign stay on this brief. The cap is creators × per-creator cap × required platforms. Payment settles when the card charge is confirmed.`,
          remaining == null
            ? "Set a participant limit on the brief. Add funds is creators × per-creator cap × required platforms, plus the platform fee."
            : fundedEnough
              ? "This campaign is funded to its total cap."
              : !payable
                ? "Staff must approve the brief before you can pay. The total on this card updates as you edit."
                : quotes.some((q) => q.allocated)
                  ? "A settled payment is already on this campaign. Remaining falls when a creator is accepted."
                  : preview?.free_run_enabled
                    ? "If staff have offered a complimentary run, you can request that instead of paying by card."
                    : "",
        ].filter(Boolean).join(" ")} />
      </div>

      {(liveTotalMinor != null || preview?.campaign_total_minor != null) ? (
        <dl style={{ margin: 0, display: "grid", gap: space(1),
                     gridTemplateColumns: "auto 1fr", fontSize: fontSize.body }}>
          <dt style={{ color: "var(--muted)" }}>Campaign total cap</dt>
          <dd style={{ margin: 0, textAlign: "right" }}>
            <Money value={minor(liveTotalMinor ?? preview!.campaign_total_minor!, ccy)} />
          </dd>
          {preview ? (
            <>
              <dt style={{ color: "var(--muted)" }}>Already paid</dt>
              <dd style={{ margin: 0, textAlign: "right" }}>
                <Money value={minor(preview.funded_minor, ccy)} />
              </dd>
            </>
          ) : null}
          {preview && Number.isFinite(transferredIn) && transferredIn > 0 ? (
            <dd style={{ margin: 0, gridColumn: "1 / -1", fontSize: fontSize.micro,
                         color: "var(--faint)", textAlign: "right", lineHeight: 1.5 }}>
              Includes <Money value={minor(preview.transferred_in_minor ?? "0", ccy)} />
              {preview.transferred_from ? ` ${preview.transferred_from}` : " transferred from another campaign"}.
            </dd>
          ) : null}
          {remaining != null ? (
            <>
              <dt style={{ color: "var(--muted)" }}>Still to fund</dt>
              <dd style={{ margin: 0, textAlign: "right" }}>
                <Money value={minor(remaining, ccy)} />
              </dd>
            </>
          ) : null}
        </dl>
      ) : null}

      {shown ? (
        <dl style={{ margin: 0, display: "grid", gap: space(1),
                     gridTemplateColumns: "auto 1fr", fontSize: fontSize.body }}>
          <dt style={{ color: "var(--muted)" }}>To the pool</dt>
          <dd style={{ margin: 0, textAlign: "right" }}>
            <Money value={minor(shown.net, shown.currency)} />
          </dd>
          <dt style={{ color: "var(--muted)" }}>
            Platform fee{preview ? ` (${preview.fee_percent}%)` : ""}
          </dt>
          <dd style={{ margin: 0, textAlign: "right" }}>
            <Money value={minor(shown.fee, shown.currency)} />
          </dd>
          {Number(shown.tax) > 0 ? (
            <>
              <dt style={{ color: "var(--muted)" }}>Tax</dt>
              <dd style={{ margin: 0, textAlign: "right" }}>
                <Money value={minor(shown.tax, shown.currency)} />
              </dd>
            </>
          ) : null}
          <dt style={{ color: "var(--text)", fontWeight: 650 }}>You pay</dt>
          <dd style={{ margin: 0, textAlign: "right", fontWeight: 650 }}>
            <Money value={minor(shown.gross, shown.currency)} />
          </dd>
        </dl>
      ) : null}

      {belowCardMin && shown ? (
        <FormError message={
          `This campaign would charge ${formatMoney(minor(shown.gross, ccy))}. Card payments cannot take less than ${
            formatMoney(minor(preview?.min_charge_minor ?? "50", ccy))
          }. Raise the per-creator cap or how many creators can join.`
        } />
      ) : null}

      {error ? <FormError message={error} /> : null}

      {canPay || canRequestFree || freeRunPending ? (
        <div style={{ display: "grid", gap: space(2) }}>
          {canPay ? (
            <Button block variant="primary" onClick={() => void pay()} disabled={busy || requesting}>
              {busy ? "Opening…" : "Pay now"}
            </Button>
          ) : null}
          {canRequestFree ? (
            <Button block variant="secondary" onClick={() => void requestFreeRun()}
              disabled={busy || requesting}>
              {requesting
                ? "Sending…"
                : freeRunStatus === "GRANTED"
                  ? "Request remaining as a free run"
                  : "Request a free run"}
            </Button>
          ) : null}
          {freeRunPending ? (
            <p style={{ margin: 0, fontSize: fontSize.micro, color: "var(--muted)", lineHeight: 1.5 }}>
              Free-run request sent. Staff will add the remaining funds without a card payment.
              {campaignState === "ACTIVE" ? "" : " You can activate it after the funds are on the pool."}
            </p>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export function BillingReturnScreen({ campaignId, quoteId, onOpen }: {
  campaignId: string | null; quoteId: string | null; onOpen: (id: string) => void;
}) {
  const [state, setState] = useState<"loading" | "pending" | "funded" | "error">("loading");
  const [detail, setDetail] = useState("Checking the payment against the ledger.");

  useEffect(() => {
    if (!campaignId || !quoteId) {
      setState("error");
      setDetail("This return link is missing the campaign or payment.");
      return;
    }
    let n = 0;
    const tick = async () => {
      const r = await api.get<{ quote: Quote }>(
        `/campaigns/${campaignId}/funding-quotes/${quoteId}`);
      if (!r.ok) {
        setState("error");
        setDetail(r.message);
        return true;
      }
      if (r.data.quote.allocated) {
        setState("funded");
        setDetail("The payment has settled. Funded and remaining now show on the campaign.");
        return true;
      }
      setState("pending");
      setDetail("Payment pending - awaiting confirmation. Refreshing this page does not fund the campaign.");
      n += 1;
      return n >= 8;
    };
    void tick();
    const id = window.setInterval(() => { void tick().then((done) => { if (done) window.clearInterval(id); }); }, 2500);
    return () => window.clearInterval(id);
  }, [campaignId, quoteId]);

  return (
    <>
      <PageHeader eyebrow="Billing" title="Payment return"
        description="We only treat a campaign as funded after the card payment is confirmed to us." />
      <StateBlock
        kind={state === "funded" ? "empty" : state === "error" ? "error" : "loading"}
        title={state === "funded" ? "Funds are on the campaign"
          : state === "error" ? "Could not confirm this payment"
          : "Waiting for confirmation"}
        detail={detail}
        action={campaignId
          ? <Button variant="primary" onClick={() => onOpen(campaignId)}>Open campaign</Button>
          : undefined}
      />
    </>
  );
}
